import React, { useEffect } from 'react';
import { logEvent as logAnalyticsEvent } from '../analytics';
import { ClinicAddressInfo, ClinicHours, ClinicInfo } from '../types';
import GenericCard, { GenericContentType } from './GenericCard';
import GenericCarousel from './GenericCarousel';
import { ReactComponent as TimeClockSvg } from '../icons/time_clock.svg';
import { ReactComponent as DirectionsPinSvg } from '../icons/directions_pin.svg';
import styles from './ClinicCardList.module.css';
import { format } from '../helpers';
import clinicConfig from '../resources/clinicConfig.json';

import { AmpFlagSet } from '../amplitude';

export interface ClinicAttachmentData {
    clinics: ClinicInfo[];
    flowType: string;
    serviceType: string;
}

function toDisplayAddress(address: ClinicAddressInfo): string {
    return address.line2
        ? `${address.line1}, ${address.line2}, ${address.city}, ${address.state} ${address.postalCode}`
        : `${address.line1}, ${address.city}, ${address.state} ${address.postalCode}`;
}

function getClinicHours(openHours: ClinicHours[] | undefined): string {
    const currentDay = new Date().toLocaleDateString(undefined, {
        weekday: 'long',
    });
    const hoursForToday = openHours?.find(
        (openDay) => openDay.day === currentDay
    );
    if (!hoursForToday) {
        return clinicConfig.providence.clinicClosed;
    }
    return `${hoursForToday.openHours.openTime}-${hoursForToday.openHours.closeTime}`;
}

const ClinicCard: React.FC<{
    clinicInfo: ClinicInfo;
    featureFlags: AmpFlagSet;
    index: number;
    clinicAttachmentContent: ClinicAttachmentData;
}> = ({ clinicInfo, featureFlags, index, clinicAttachmentContent }) => {
    const { flowType, serviceType } = clinicAttachmentContent;
    const distanceDisplay = clinicInfo.distance;

    useEffect(() => {
        if (index === 0) {
            logAnalyticsEvent(
                serviceType
                    ? `${serviceType} - ZipCode Entered`
                    : 'ZipCode Entered',
        {nearest_clinic_radius: distanceDisplay ? Math.round(distanceDisplay) : undefined} // eslint-disable-line
            );
        }
    }, [index, serviceType, distanceDisplay]);

    return (
        <GenericCard
            data={{
                title: { text: clinicInfo.displayName },
                subtitles: [{ text: clinicInfo.address.city }],
                content: [
                    {
                        type: GenericContentType.Info,
                        title: { text: toDisplayAddress(clinicInfo.address) },
                        info: {
                            text: distanceDisplay
                                ? format(clinicConfig.providence.distanceAway, {
                                      distance:
                                          Math.round(distanceDisplay * 10) / 10,
                                  })
                                : clinicConfig.providence.distanceAwayUnknown,
                        },
                        image: (
                            <DirectionsPinSvg
                                className={styles.clinicCardContentIcon}
                            />
                        ),
                    },
                    {
                        type: GenericContentType.Info,
                        title: {
                            text: clinicConfig.providence.clinicHours,
                        },
                        info: {
                            text: getClinicHours(clinicInfo.openDays),
                        },
                        image: (
                            <TimeClockSvg
                                className={styles.clinicCardContentIcon}
                            />
                        ),
                    },
                    {
                        type: GenericContentType.Disclaimer,
                        disclaimer: clinicConfig.providence.disclaimer,
                    },
                ],
                buttonData: {
                    title: clinicConfig.providence.learnAboutClinic,
                    url: clinicInfo.url,
                    amplitudeEvent: {
                        eventType: serviceType
                            ? `${serviceType} - Clinic Selected`
                            : 'Clinic Selected',
                        eventProperties: {
              clinic_card_position: index, // eslint-disable-line
              clinic_name: clinicInfo.displayName, // eslint-disable-line
              flow_type: flowType, // eslint-disable-line
                        },
                    },
                    insightsEvent: {
                        name: 'ClinicScheduleButtonClick',
                        properties: { url: clinicInfo.url },
                    },
                },
            }}
            featureFlags={featureFlags}
        />
    );
};

const ClinicCardList: React.FC<{
    clinicData: ClinicAttachmentData;
    featureFlags: AmpFlagSet;
}> = ({ clinicData, featureFlags }) => {
    const clinicCards = clinicData.clinics.map((clinicInfo, index) => (
        <ClinicCard
            clinicInfo={clinicInfo}
            featureFlags={featureFlags}
            index={index}
            key={clinicInfo.displayName}
            clinicAttachmentContent={clinicData}
        />
    ));
    return <GenericCarousel elements={clinicCards} />;
};

export default ClinicCardList;
